import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InfoIcon from "@mui/icons-material/Info";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationType,
  validationMessages,
} from "../../actions/validations";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { sampleTestBlockMessage } from "../../../constants/sncSampleGuard";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SnCPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  SNC_TOOLTIP_BY_SCREEN,
  sncTooltipGroupKey,
  type SnCStandardTooltipGroup,
} from "../../../constants/SnCTooltipInformation";
import { useTooltipText } from "../../../tooltips/TooltipContext";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

const CODE_FIELD = "Holiday/Hot Date Code";
const NAME_FIELD = "Holiday/Hot Date Name";
const START_DATE_FIELD = "Start Date";
const END_DATE_FIELD = "End Date";

const DISPLAY_LOCATION_OPTIONS = [
  "All",
  "Guest (GRC/Availability)",
  "Function Book",
] as const;

const DISPLAY_LINE_OPTIONS = ["Holidays", "Hot Dates"] as const;

const tomorrow = () => dayjs().startOf("day").add(1, "day");

const SnCHolidaysAndHotDates = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const sncTipGroup = sncTooltipGroupKey("Holidays and Hot Dates");
  const sncTipDefaults = SNC_TOOLTIP_BY_SCREEN["Holidays and Hot Dates"] as SnCStandardTooltipGroup;
  const tipHeader = useTooltipText(sncTipGroup, "header", sncTipDefaults.header);
  const tipCode = useTooltipText(sncTipGroup, "code", sncTipDefaults.code);
  const tipName = useTooltipText(sncTipGroup, "name", sncTipDefaults.name);
  const tipActive = useTooltipText(sncTipGroup, "active", sncTipDefaults.active);
  const [holidayCode, setHolidayCode] = useState<string | null>(null);
  const [holidayName, setHolidayName] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [numberOfNights, setNumberOfNights] = useState<number | "">(0);
  const [displayLocation, setDisplayLocation] = useState<string | null>("All");
  const [displayLine, setDisplayLine] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [codeFieldError, setCodeFieldError] = useState("");
  const [nameFieldError, setNameFieldError] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [nightsError, setNightsError] = useState(false);
  const [displayLocationError, setDisplayLocationError] = useState(false);
  const [displayLineError, setDisplayLineError] = useState(false);

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateInput = (): boolean => {
    setCodeFieldError("");
    setNameFieldError("");
    setStartDateError("");
    setEndDateError("");
    setNightsError(false);
    setDisplayLocationError(false);
    setDisplayLineError(false);

    if (!holidayCode) {
      setCodeFieldError(CODE_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!holidayName) {
      setNameFieldError(NAME_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!startDate) {
      setStartDateError(START_DATE_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (!endDate) {
      setEndDateError(END_DATE_FIELD + validationMessages.sncMandatorySuffix);
    }
    if (numberOfNights === "" || numberOfNights === null) {
      setNightsError(true);
    }
    if (!displayLocation) {
      setDisplayLocationError(true);
    }
    if (!displayLine) {
      setDisplayLineError(true);
    }

    if (
      !holidayCode ||
      !holidayName ||
      !startDate ||
      !endDate ||
      numberOfNights === "" ||
      numberOfNights === null ||
      !displayLocation ||
      !displayLine
    ) {
      setAlertMessage(validationMessages.sncAllMandatory);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      !validateString(
        holidayCode,
        validationLength.sncHolidayCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(validationMessages.sncHolidayCode);
      return false;
    }
    if (
      !validateString(
        holidayName,
        validationLength.sncHolidayName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(validationMessages.sncHolidayName);
      return false;
    }
    const nightsNum = Number(numberOfNights);
    if (
      !Number.isFinite(nightsNum) ||
      !validateIntegerLength(nightsNum, validationLength.sncHolidayNights)
    ) {
      setNightsError(true);
      setAlertMessage(validationMessages.sncHolidayNights);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    const today = dayjs().startOf("day");
    if (!startDate.isAfter(today)) {
      setStartDateError("Start Date must be after the present date");
      setAlertMessage("Start Date must be after the present date");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (endDate.isBefore(startDate, "day")) {
      setEndDateError("End Date must be on or after Start Date");
      setAlertMessage("End Date must be on or after Start Date");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    const sampleBlock = sampleTestBlockMessage(holidayCode, holidayName, displayLocation, displayLine, startDate ? startDate.format("YYYY-MM-DD") : null, endDate ? endDate.format("YYYY-MM-DD") : null);
    if (sampleBlock) {
      setAlertMessage(sampleBlock);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const payload = {
    [CODE_FIELD]: holidayCode,
    [NAME_FIELD]: holidayName,
    [START_DATE_FIELD]: startDate ? startDate.format("YYYY-MM-DD") : null,
    [END_DATE_FIELD]: endDate ? endDate.format("YYYY-MM-DD") : null,
    "Number Of Nights":
      numberOfNights === "" ? 0 : Number(numberOfNights),
    "Display Location": displayLocation,
    "Display Line": displayLine,
    Active: active ? "Yes" : "No",
  };

  const updateItem = () => {
    if (!validateInput()) {
      return;
    }
    const otherItems = (items ?? []).filter(
      (item: any) => item.id !== selectedId,
    );
    if (checkDuplicate(otherItems, CODE_FIELD, holidayCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      (prev ?? []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${SnCPropertyEnum.holidaysAndHotDates} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(items ?? [], CODE_FIELD, holidayCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }

    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: (prev?.length ?? 0) + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setCodeFieldError("");
      setNameFieldError("");
      setStartDateError("");
      setEndDateError("");
      setNightsError(false);
      setDisplayLocationError(false);
      setDisplayLineError(false);
      setHolidayCode(list[CODE_FIELD] ?? null);
      setHolidayName(list[NAME_FIELD] ?? null);
      setStartDate(
        list[START_DATE_FIELD] ? dayjs(list[START_DATE_FIELD]) : null,
      );
      setEndDate(list[END_DATE_FIELD] ? dayjs(list[END_DATE_FIELD]) : null);
      setNumberOfNights(
        list["Number Of Nights"] === "" || list["Number Of Nights"] == null
          ? 0
          : Number(list["Number Of Nights"]),
      );
      setDisplayLocation(list["Display Location"] ?? "All");
      setDisplayLine(list["Display Line"] ?? null);
      setActive(list.Active !== "No" && list.Active !== false);
    } else {
      setHolidayCode(null);
      setHolidayName(null);
      setStartDate(null);
      setEndDate(null);
      setNumberOfNights(0);
      setDisplayLocation("All");
      setDisplayLine(null);
      setActive(true);
      setCodeFieldError("");
      setNameFieldError("");
      setStartDateError("");
      setEndDateError("");
      setNightsError(false);
      setDisplayLocationError(false);
      setDisplayLineError(false);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items ?? [], id));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="column" gap="10px">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" sx={textStyle}>
              Holidays and Hot Dates
              <Tooltip
                title={tooltipComponent(tipHeader)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" alignItems="center">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editItem(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton click={() => editItem(lastUpdatedRecord)} />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active"
              />
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="20px">
            <Stack
              direction="row"
              className="form-two-col-row"
              flexWrap="wrap"
              gap="20px"
            >
              <Stack direction="column" className="form-two-col-field">
                <FormLabel component="legend">
                  Holiday/Hot Date Code *
                  <Tooltip
                    title={tooltipComponent(tipCode)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  required
                  variant="outlined"
                  size="small"
                  placeholder="Holiday/Hot Date Code"
                  value={holidayCode ?? ""}
                  error={!!codeFieldError}
                  helperText={codeFieldError}
                  onChange={(e) => {
                    setHolidayCode(e.target.value || null);
                    setCodeFieldError("");
                  }}
                />
              </Stack>
              <Stack direction="column" className="form-two-col-field">
                <FormLabel component="legend">
                  Holiday/Hot Date Name *
                  <Tooltip
                    title={tooltipComponent(tipName)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  required
                  variant="outlined"
                  size="small"
                  placeholder="Holiday/Hot Date Name"
                  value={holidayName ?? ""}
                  error={!!nameFieldError}
                  helperText={nameFieldError}
                  onChange={(e) => {
                    setHolidayName(e.target.value || null);
                    setNameFieldError("");
                  }}
                />
              </Stack>
            </Stack>
            <Stack
              direction="row"
              className="form-two-col-row"
              flexWrap="wrap"
              gap="20px"
            >
              <Stack direction="column" className="form-two-col-field">
                <FormLabel component="legend">
                  Start *
                  <Tooltip
                    title={tooltipComponent(tipName)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={startDate}
                  minDate={tomorrow()}
                  onChange={(v) => {
                    setStartDate(v);
                    setStartDateError("");
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: !!startDateError,
                      helperText: startDateError,
                    },
                  }}
                />
              </Stack>
              <Stack direction="column" className="form-two-col-field">
                <FormLabel component="legend">
                  End *
                  <Tooltip
                    title={tooltipComponent(tipName)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={endDate}
                  minDate={startDate ?? tomorrow()}
                  onChange={(v) => {
                    setEndDate(v);
                    setEndDateError("");
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: !!endDateError,
                      helperText: endDateError,
                    },
                  }}
                />
              </Stack>
            </Stack>
            <Stack
              direction="row"
              className="form-two-col-row"
              flexWrap="wrap"
              gap="20px"
            >
              <Stack direction="column" className="form-two-col-field">
                <FormLabel component="legend">
                  Number Of Nights *
                  <Tooltip
                    title={tooltipComponent(tipName)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <TextField
                  required
                  type="number"
                  variant="outlined"
                  size="small"
                  placeholder="0"
                  value={numberOfNights}
                  error={nightsError}
                  helperText={
                    nightsError ? validationMessages.fieldRequired : ""
                  }
                  inputProps={{ min: 0 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNumberOfNights(value === "" ? "" : Number(value));
                    setNightsError(false);
                  }}
                />
              </Stack>
              <Stack direction="column" className="form-two-col-field">
                <FormLabel component="legend">
                  Display Location *
                  <Tooltip
                    title={tooltipComponent(tipName)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <Autocomplete
                  size="small"
                  options={[...DISPLAY_LOCATION_OPTIONS]}
                  value={displayLocation}
                  onChange={(_, v) => {
                    setDisplayLocation(v);
                    setDisplayLocationError(false);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Search by Display Location"
                      error={displayLocationError}
                      helperText={
                        displayLocationError
                          ? validationMessages.fieldRequired
                          : ""
                      }
                    />
                  )}
                />
              </Stack>
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <FormLabel component="legend">
                Display Line *
                <Tooltip title={tooltipComponent(tipName)}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                size="small"
                options={[...DISPLAY_LINE_OPTIONS]}
                value={displayLine}
                onChange={(_, v) => {
                  setDisplayLine(v);
                  setDisplayLineError(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search by Display Line"
                    error={displayLineError}
                    helperText={
                      displayLineError ? "Missing Display Line" : ""
                    }
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() => (selectedId ? updateItem() : addItem())}
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Holiday/Hot
              Date
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">
              Holidays and Hot Dates Available
            </Typography>
            {items?.length ? (
              <DraggableItemList
                itemData={items}
                onOrderUpdate={(order) => setItems(order)}
                onEdit={editItem}
                onDelete={deleteItemById}
                code={CODE_FIELD}
                name={NAME_FIELD}
              />
            ) : (
              <Typography variant="body2">
                No Holidays and Hot Dates Available
              </Typography>
            )}
          </Stack>
        </Paper>
        <AlertComponent
          message={alertMessage}
          type={alertType}
          alertOpen={openAlert}
          closeAlert={() => setOpenAlert(false)}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default SnCHolidaysAndHotDates;
